import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, Clock, MapPin, Plane } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CITY_PAGES } from "@/lib/data";
import { useLang } from "@/lib/i18n";

const ease = [0.22, 1, 0.36, 1];

const bookUrl = (from, to) =>
  `/reservation?from=${encodeURIComponent(from)}${to ? `&to=${encodeURIComponent(to)}` : ""}`;

const FadeIn = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function CityPage() {
  const { slug } = useParams();
  const { t } = useLang();
  const cfg = CITY_PAGES[slug];
  const city = cfg ? t(`ct.city_pages.${slug}`) : null;

  useEffect(() => {
    if (!cfg || !city) return undefined;
    const originalTitle = document.title;
    document.title = `${city.h1} — MILES`;
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [kind, name] = attr;
        el.setAttribute(kind, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    const url = `${window.location.origin}/villes/${slug}`;
    setMeta('meta[name="description"]', ["name", "description"], city.sub);
    setMeta('meta[property="og:title"]', ["property", "og:title"], `${city.h1} — MILES`);
    setMeta('meta[property="og:description"]', ["property", "og:description"], city.sub);
    setMeta('meta[property="og:url"]', ["property", "og:url"], url);
    setMeta('meta[property="og:type"]', ["property", "og:type"], "website");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
    const jsonLd = document.createElement("script");
    jsonLd.setAttribute("type", "application/ld+json");
    jsonLd.setAttribute("data-city-seo", slug);
    jsonLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: city.h1,
      provider: { "@type": "Organization", name: "MILES", url: window.location.origin },
      areaServed: { "@type": "City", name: cfg.name },
      url,
      offers: { "@type": "Offer", priceCurrency: "CHF", price: cfg.routes[0].price.replace(/[^0-9.]/g, "") },
    });
    document.head.appendChild(jsonLd);
    return () => {
      document.title = originalTitle;
      jsonLd.remove();
    };
  }, [cfg, city, slug]);

  if (!cfg) return <Navigate to="/villes" replace />;

  const others = Object.entries(CITY_PAGES).filter(([key]) => key !== slug);

  return (
    <div className="min-h-screen bg-white text-ink" data-testid={`city-page-${slug}`}>
      <Navbar />

      <section className="bg-gradient-to-b from-miles-ice/60 to-white pb-16 pt-32 lg:pb-20 lg:pt-40" data-testid="city-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn y={14}>
            <Link to="/villes" data-testid="city-back"
              className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate transition-colors hover:text-miles">
              <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1.5} />
              {t("ct.city_pages.back")}
            </Link>
          </FadeIn>
          <div className="mt-8 grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <FadeIn y={20}>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{city.tag}</p>
              <h1 className="mt-5 text-4xl font-semibold leading-[1.02] tracking-[-0.05em] sm:text-5xl lg:text-6xl">{city.h1}</h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate sm:text-lg">{city.sub}</p>
              <div className="mt-8 flex flex-wrap gap-x-7 gap-y-3 border-t border-ink/10 pt-6" data-testid="city-trust">
                {t("ct.trust").map((item) => (
                  <p key={item} className="flex items-center gap-2.5 text-sm font-medium text-ink/80">
                    <Check className="h-4 w-4 shrink-0 text-miles" strokeWidth={2} /> {item}
                  </p>
                ))}
              </div>
              <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3">
                <Link to={bookUrl(cfg.name)} data-testid="city-hero-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-miles px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(36,87,255,0.3)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-dark">
                  {t("ct.book").replace("{city}", cfg.name)} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </Link>
                <p className="inline-flex items-center gap-1.5 font-mono text-sm font-semibold text-ink" data-testid="city-airport-price">
                  <Plane className="h-4 w-4 text-miles" strokeWidth={1.5} />
                  {t("ct.airport_from").replace("{code}", cfg.airport).replace("{price}", cfg.routes[0].price)}
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.12}>
              <div className="overflow-hidden rounded-[2rem] border border-ink/10 shadow-[0_40px_90px_-50px_rgba(11,31,77,0.4)]">
                <img src={cfg.image} alt={city.h1} className="img-clean aspect-[4/3] w-full object-cover" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20" data-testid="city-airport-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <div className="grain relative flex flex-wrap items-center gap-x-10 gap-y-5 overflow-hidden rounded-[2rem] bg-miles-deep px-7 py-8 sm:px-10">
              <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-miles/25 blur-3xl" />
              <div className="relative">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-miles-sky">{t("ct.city_pages.airport_t")}</p>
                <p className="mt-2 font-mono text-4xl font-bold tracking-tight text-white">{cfg.airport}</p>
              </div>
              <p className="relative max-w-md text-sm leading-relaxed text-white/70">{city.airport_note}</p>
              <Link to={bookUrl(cfg.name, cfg.routes[0].to)} data-testid="city-airport-cta"
                className="relative ml-auto inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-miles-deep transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-ice">
                {t("ct.city_pages.estimate")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="pb-20 pt-4 lg:pb-24" data-testid="city-routes">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("ct.city_pages.routes_t")}</p>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate">{t("ct.city_pages.routes_s")}</p>
          </FadeIn>
          <div className="mt-8 border-t border-ink/10">
            {cfg.routes.map((route, i) => {
              const [label, duration] = city.route_labels[i];
              return (
                <FadeIn key={route.to} delay={i * 0.05} y={14}>
                  <div className="group grid gap-2 border-b border-ink/10 py-6 sm:grid-cols-[1.4fr_0.5fr_0.7fr_auto] sm:items-center sm:gap-6"
                    data-testid={`city-route-${i}`}>
                    <p className="flex items-center gap-3 text-lg font-semibold tracking-tight text-ink transition-transform duration-300 group-hover:translate-x-1.5 sm:text-xl">
                      <MapPin className="h-4 w-4 shrink-0 text-miles" strokeWidth={1.5} /> {label}
                    </p>
                    <p className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.14em] text-slate">
                      <Clock className="h-3.5 w-3.5 text-miles" strokeWidth={1.5} /> {duration}
                    </p>
                    <p className="font-mono text-sm font-semibold text-miles">{t("ct.city_pages.from_word")} {route.price}</p>
                    <Link to={bookUrl(route.from, route.to)} data-testid={`city-route-book-${i}`}
                      className="group/link inline-flex w-fit items-center gap-2 rounded-full border border-ink/10 px-4 py-2 text-xs font-semibold text-ink transition-colors hover:border-miles hover:text-miles">
                      {t("ct.city_pages.estimate")}
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/link:-translate-y-0.5 group-hover/link:translate-x-0.5" strokeWidth={1.5} />
                    </Link>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-cloud py-20 lg:py-24" data-testid="city-places">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("ct.city_pages.places_t")}</p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {city.places.map((place) => (
                <span key={place} className="rounded-full border border-ink/10 bg-white px-4 py-2 text-sm font-medium text-ink/80"
                  data-testid={`city-place-${place.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {place}
                </span>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-12 font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("ct.city_pages.others_t")}</p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {others.map(([key, other]) => (
                <Link key={key} to={`/villes/${key}`} data-testid={`city-other-${key}`}
                  className="group rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles">
                  {other.name}
                </Link>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-miles-deep py-24 lg:py-32" data-testid="city-final">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-miles/25 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles-sky">{t("ct.final_eyebrow")}</p>
            <h2 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-white sm:text-5xl lg:text-6xl">
              {t("ct.final_h2a")}
              <br />
              <span className="font-light text-white/60">{t("ct.final_h2b")}</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">{t("ct.final_p")}</p>
            <Link to={bookUrl(cfg.name)} data-testid="city-final-cta"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-miles px-8 py-4 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(36,87,255,0.4)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-dark">
              {t("ct.final_cta")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
